import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import { MetricResult } from '../../../../api/types';
import { MetricId } from '../../../../api/types-graphql';
import {
  useGlobalLocaleContext,
  useMetricDetailsMapContext,
} from '../../../../context/contexts';
import { MetricIdTypeArrays } from '../../../../types';
import { formatMetricValue } from '../../../../utils-display';
import { useDataFieldValueFormatter } from '../../../hooks';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  useChartTitleForGroupByChart,
  useMetricDisplayValues,
  useMetricQueries,
  useTitleConfigWithSubtitle,
} from '../common/hooks';
import {
  DataOrError,
  ExternalChartComponentProps,
  ValidationResponse,
} from '../common/types';
import {
  TimePeriodUnsegmentedVizConfig,
  TimePeriodUnsegmentedVizData,
  TimePeriodUnsegmentedVizDataResponse,
  TimePeriodUnsegmentedVizExternalProps,
  TimePeriodUnsegmentedVizViews,
} from './types';
import {
  buildTimePeriodPieBenchmarkSubRows,
  getMetricQueriesForTimePeriodUnsegmentedWithOptionalPieBenchmarks,
} from './utils/query-and-benchmark-utils';

const validateResponseHasCorrectSegmentsForUnsegmented = (
  metricResults: MetricResult[],
  config: TimePeriodUnsegmentedVizConfig
): ValidationResponse => {
  const { groupBy } = config;
  try {
    metricResults.forEach((metricResult) => {
      const groupSegments = metricResult.segments[0].groupSegments;
      groupSegments.forEach((groupSegment) => {
        if (groupSegment.groupSegment.length !== 1) {
          throw new Error(
            'Error: metricResult for multiple queries should have only one group segment'
          );
        }
        if (
          !isEqual(
            {
              dataType: groupSegment.groupSegment[0].dataType,
              dataField: groupSegment.groupSegment[0].dataField,
            },
            groupBy.dataField
          )
        ) {
          throw new Error('Error: groupBy field does not match');
        }
      });
    });
  } catch (e) {
    return {
      isValid: false,
      error: new Error(
        `Error: metricResult does not have correct segments: ${String(e)}`
      ),
    };
  }
  return { isValid: true, error: null };
};

type ChartTitleMultipleQueryProps = {
  config: TimePeriodUnsegmentedVizConfig;
  response: TimePeriodUnsegmentedVizDataResponse | null;
  externalProps: ExternalChartComponentProps;
};

const useChartTitleUnsegmented = (
  props: ChartTitleMultipleQueryProps
): TitleConfig => {
  const { config, response } = props;
  const { queries } = config;
  const metrics = queries.map((q) => q.metric);
  return useChartTitleForGroupByChart({
    externalProps: props.externalProps,
    metrics: metrics as MetricIdTypeArrays,
    filters: '',
    timeSelection: config.timeSelection,
    metricsSql: response?.responseForMetrics.map((r) => r.metricSql) ?? [],
    segmentations: [config.groupBy],
    titleFromConfig: config.title,
  });
};

const useChartTimePeriodUnsegmentedResponse = (
  config: TimePeriodUnsegmentedVizConfig,
  externalProps: TimePeriodUnsegmentedVizExternalProps
): DataOrError<{
  dataResponse: TimePeriodUnsegmentedVizDataResponse;
  pieBenchmarkSubRows?: ReturnType<typeof buildTimePeriodPieBenchmarkSubRows>;
}> => {
  const { timeSliderState, segmentationState, filtersState } = externalProps;
  const { queries, timeSelection, groupBy, filters, benchmarkConfigs } = config;
  const { t } = useTranslation();
  const metricDetailsMap = useMetricDetailsMapContext();
  const dataFieldValueFormatter = useDataFieldValueFormatter();

  const fetchPieBenchmarks = Boolean(
    benchmarkConfigs?.length &&
      externalProps.selectedView === TimePeriodUnsegmentedVizViews.PIE_CHART &&
      queries.length === 1
  );

  const queriesToFetch =
    getMetricQueriesForTimePeriodUnsegmentedWithOptionalPieBenchmarks({
      queries,
      timeSelection,
      groupBy,
      filters,
      benchmarkConfigs,
      includePieBenchmarks: fetchPieBenchmarks,
    });

  const { queryResponses } = useMetricQueries({
    queries: queriesToFetch,
    timeSliderState,
    segmentationState,
    filtersState,
  });

  const firstError = queryResponses.find((r) => r.error)?.error;
  if (firstError) {
    return { data: null, error: firstError };
  }

  const nMain = queries.length;
  const nBench = fetchPieBenchmarks ? (benchmarkConfigs?.length ?? 0) : 0;
  const mainQueryResponses = queryResponses.slice(0, nMain);
  const benchQueryResponses = queryResponses.slice(nMain, nMain + nBench);

  const firstMetricResults = mainQueryResponses.map(
    (r) => (r.data as MetricResult[])[0]
  );

  const { isValid, error: validationError } =
    validateResponseHasCorrectSegmentsForUnsegmented(
      firstMetricResults,
      config
    );
  if (!isValid) {
    return { data: null, error: validationError };
  }

  const response: TimePeriodUnsegmentedVizDataResponse = {
    groupBy,
    responseForMetrics: firstMetricResults.map((metricResult, i) => {
      const { metric } = queries[i];
      const groupSegmentsWithValues =
        metricResult.segments[0].groupSegments.filter(
          (groupSegment) => Number(groupSegment.data) !== 0
        );
      return {
        metric,
        groups: groupSegmentsWithValues.map((groupSegment) => {
          return {
            groupValue: groupSegment.groupSegment.map(
              (gs) => gs.value as string | null
            ),
            total: groupSegment.data as number,
          };
        }),
        metricSql: metricResult.meta.metricSql,
      };
    }),
  };

  let pieBenchmarkSubRows:
    | ReturnType<typeof buildTimePeriodPieBenchmarkSubRows>
    | undefined;

  if (fetchPieBenchmarks && benchmarkConfigs?.length) {
    const benchErrors = benchQueryResponses.some((r) => r.error);
    if (!benchErrors) {
      const mainMetricResult = firstMetricResults[0];
      const benchmarkMetricResults = benchQueryResponses.map(
        (r) => (r.data as MetricResult[])[0]
      );
      const { metric } = queries[0];
      const metricDetails = metricDetailsMap[metric.value];
      if (metricDetails) {
        const valueFormatter = (value: number) =>
          formatMetricValue(value, metricDetails.formatType);
        pieBenchmarkSubRows = buildTimePeriodPieBenchmarkSubRows({
          mainMetricResult,
          benchmarkMetricResults,
          benchmarkConfigs,
          groupByDataField: groupBy.dataField,
          dataFieldValueFormatter,
          valueFormatter,
          t,
        });
      }
    }
  }

  return {
    data: { dataResponse: response, pieBenchmarkSubRows },
    error: null,
  };
};

export const useTimePeriodUnsegmentedConfigToData = (
  config: TimePeriodUnsegmentedVizConfig,
  externalProps: TimePeriodUnsegmentedVizExternalProps
): DataOrError<TimePeriodUnsegmentedVizData> => {
  const { data, error } = useChartTimePeriodUnsegmentedResponse(
    config,
    externalProps
  );
  const baseTitleConfig = useChartTitleUnsegmented({
    config,
    response: data?.dataResponse ?? null,
    externalProps,
  });
  const titleConfig = useTitleConfigWithSubtitle(
    baseTitleConfig,
    config.subtitle
  );
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricDisplayValues = useMetricDisplayValues(
    config.queries.map((q) => q.metric.value as unknown as MetricId)
  );
  const locale = useGlobalLocaleContext();

  const pieFooter =
    externalProps.selectedView === TimePeriodUnsegmentedVizViews.PIE_CHART
      ? config.footer?.[locale.selected]
      : undefined;

  if (error) {
    return { data: null, error };
  }

  return {
    data: {
      dataResponse: data.dataResponse,
      titleConfig,
      metricDisplayValues,
      metricDetails: metricDetailsMap,
      chartColors: config.chartColors,
      pieBenchmarkSubRows: data.pieBenchmarkSubRows,
      pieFooter,
    },
    error: null,
  };
};
